// SIGN-OFF STATE ON ANY DATE, AND A TILE'S COLOUR (docs SIGNOFF-SPEC; the reference
// crate's state.rs, in JS so scrubbing never waits on the server).
//
// Levels: 0 grey (not signed off) < 1 blue (owner/GC) < 2 gold (contractor; stored as "tenant"). A target holds
// each star from its sign-off until a withdrawal; it is at the highest star it holds.
// An element is as signed off as its area, a larger area around it, its level, or one of
// its systems signed off on one of those areas, or a 3D volume its tile is in (or one of
// its systems in that volume) - whichever is highest. A tile is the LOWEST of its elements
// (conservative), and partial when they differ; the blue and gold shares say how much of
// it is how far. Outside a volume only the PLACED elements count; a tile in a volume
// counts all of them (tile.ug / tile.u: the ones in no drawn area), since the volume
// holds them. The server's back/utils/signoffState.js is the same rule - keep them together.
export const GREY = 0;
export const BLUE = 1;
export const GOLD = 2;
export const COLORS = { [GREY]: "#d9dde3", [BLUE]: "#2f6fe4", [GOLD]: "#d4a72c" };
export const RING = { ownerGc: "#2f5fd0", tenant: "#c99a06", partner: "#a3acb9", export: "#8a94a6", withdrawn: "#c0392b" };

const slot = (level) => (level === "tenant" ? 1 : 0);
export const targetKey = (t) => {
  if (!t) return "";
  if (t.kind === "level") return `level|${t.levelKey}`;
  if (t.kind === "area") return `area|${t.areaSetId}`;
  if (t.kind === "system") return `system|${t.areaSetId}|${String(t.system || "").toLowerCase()}`;
  if (t.kind === "volume") return t.system ? `volsys|${t.volumeId}|${String(t.system).toLowerCase()}` : `volume|${t.volumeId}`;
  return "";
};

/** target key -> GREY | BLUE | GOLD, from the rings at or before `at`. */
export function stateAt(rings, at) {
  const t = at instanceof Date ? at.getTime() : new Date(at).getTime();
  const held = new Map();
  const list = (rings || []).filter((r) => r.type === "signoff" && r.target && new Date(r.at).getTime() <= t)
    .sort((a, b) => new Date(a.at) - new Date(b.at));
  for (const r of list) {
    // a trade partner's silver star is listed, never a tile colour
    if (r.level !== "ownerGc" && r.level !== "tenant") continue;
    const k = targetKey(r.target);
    if (!k) continue;
    const h = held.get(k) || [false, false];
    h[slot(r.level)] = !r.withdrawn;
    held.set(k, h);
  }
  const out = new Map();
  for (const [k, h] of held) out.set(k, h[1] ? GOLD : h[0] ? BLUE : GREY);
  return out;
}

/** What one group of a tile's elements is at: its level, its area or a larger one around it, or one of its systems on those areas. */
function groupLevel(g, states, vols = []) {
  let best = g.l ? (states.get(`level|${g.l}`) || GREY) : GREY;
  for (const v of vols) {
    const s = states.get(`volume|${v}`) || GREY;
    if (s > best) best = s;
    for (const sys of g.s || []) {
      const x = states.get(`volsys|${v}|${String(sys).toLowerCase()}`) || GREY;
      if (x > best) best = x;
    }
  }
  for (const a of [g.a, ...(g.aa || [])]) {
    if (!a) continue;
    const s = states.get(`area|${a}`) || GREY;
    if (s > best) best = s;
    for (const sys of g.s || []) {
      const v = states.get(`system|${a}|${String(sys).toLowerCase()}`) || GREY;
      if (v > best) best = v;
    }
  }
  return best;
}

/** The tile's element groups: the placed ones, and in a volume the unplaced ones too. */
export function groupsOf(tile, vols = []) {
  const groups = tile.g || [];
  if (!vols.length || !tile.u) return groups;
  const ug = (tile.ug || []).map((x) => ({ a: "", l: "", s: x.s || [], n: x.n }));
  const rest = tile.u - ug.reduce((s, x) => s + x.n, 0);
  if (rest > 0) ug.push({ a: "", l: "", n: rest });
  return [...groups, ...ug];
}

/** { level, blue, gold, partial } for a tile; grey when nothing in it counts. `vols`: the volumes holding it. */
export function tileColor(tile, states, vols = []) {
  const groups = groupsOf(tile, vols);
  const placed = groups.reduce((s, g) => s + g.n, 0);
  if (!placed) return { level: GREY, blue: 0, gold: 0, partial: false };
  let lo = GOLD; let hi = GREY; let blue = 0; let gold = 0;
  for (const g of groups) {
    const l = groupLevel(g, states, vols);
    if (l < lo) lo = l;
    if (l > hi) hi = l;
    if (l >= BLUE) blue += g.n;
    if (l >= GOLD) gold += g.n;
  }
  return { level: lo, blue: Math.round((blue / placed) * 1000) / 1000, gold: Math.round((gold / placed) * 1000) / 1000, partial: lo !== hi };
}

/** tile key -> [volume ids], from the timeline's volumes. */
export function volumesByTile(volumes) {
  const out = new Map();
  for (const v of volumes || []) for (const k of v.tiles || []) { if (!out.has(k)) out.set(k, []); out.get(k).push(v.id); }
  return out;
}

/** The innermost volume of a tile (a sub-area before its area), for "Colour by: Volumes". */
export function innermost(ids, byId) {
  let best = null;
  let depth = -1;
  for (const id of ids || []) {
    let d = 0;
    let v = byId.get(id);
    while (v && v.parentId && d < 20) { d += 1; v = byId.get(v.parentId); }
    if (d > depth) { depth = d; best = byId.get(id); }
  }
  return best;
}

/** Does a tile hold this target (any element of it)? `vols`: the volumes holding the tile. */
export function tileHolds(tile, target, vols = []) {
  if (!target) return false;
  if (target.kind === "volume") {
    if (!vols.includes(target.volumeId)) return false;
    if (!target.system) return true;
    const want = String(target.system).toLowerCase();
    return groupsOf(tile, vols).some((g) => (g.s || []).some((x) => x.toLowerCase() === want));
  }
  return (tile.g || []).some((g) => {
    if (target.kind === "level") return g.l === target.levelKey;
    const inArea = !!target.areaSetId && (g.a === target.areaSetId || (g.aa || []).includes(target.areaSetId));
    if (target.kind === "system") return inArea && (g.s || []).some((x) => x.toLowerCase() === String(target.system || "").toLowerCase());
    return inArea;
  });
}

/** Tiles a ring's target covers. `volsOf`: volumesByTile(...) */
export const tilesOf = (tiles, target, volsOf = null) => (tiles || []).filter((t) => tileHolds(t, target, (volsOf && volsOf.get(t.key)) || []));

/** Counts per colour on a date. */
export function legendCounts(tiles, states, volsOf = null) {
  const c = { [GREY]: 0, [BLUE]: 0, [GOLD]: 0 };
  for (const t of tiles || []) c[tileColor(t, states, (volsOf && volsOf.get(t.key)) || []).level] += 1;
  return c;
}

/** "27th" */
export function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}
